import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ExploreUnknownGridOverlay from '../../../ExploreUnknownGridOverlay';
import { playSoundEffect } from '../../../hooks/useSoundEffects';
import BeyondBirdTreeSlotEditor from './BeyondBirdTreeSlotEditor';
import BeyondBirdPopImage from './BeyondBirdPopImage';
import {
  BEYOND_BIRD_MEMORY_BACKGROUND_SRC,
  BEYOND_BIRD_MEMORY_TREE_SLOTS,
  BEYOND_BIRD_MEMORY_LOGICAL_WIDTH,
  BEYOND_BIRD_MEMORY_LOGICAL_HEIGHT,
  BEYOND_BIRD_MEMORY_APPEAR_SOUND_SRC,
  getBeyondBirdMemoryAcceleratingStepTimings,
} from './beyondBirdMemoryLayout';
import './BeyondBirdMemoryMinigame.css';

/**
 * @param {object} props
 * @param {{ sequence: { treeIndex: number, birdId: number }[], targetBirdId: number, lastTreeIndex: number }} props.challenge
 * @param {(pickedTreeIndex: number) => void} props.onPickTree
 * @param {boolean} props.disabled
 * @param {boolean} props.showDevGrid
 * @param {React.RefObject<HTMLElement>} props.containerRef
 * @param {Array<{ id: number, center: [number, number], hitRadius: number }> | undefined} props.treeSlots
 * @param {boolean} props.slotEditMode
 * @param {(index: number, center: [number, number], hitRadius: number) => void} props.onSlotAuthoringChange
 * @param {null | 'won' | 'lost'} props.resultOutcome
 * @param {(() => void) | undefined} props.onResultProceed
 * @param {{ id: string } | null} props.winReward
 */
export default function BeyondBirdMemoryMinigame({
  challenge,
  onPickTree,
  disabled = false,
  showDevGrid = false,
  containerRef,
  treeSlots,
  slotEditMode = false,
  onSlotAuthoringChange,
  resultOutcome = null,
  onResultProceed,
  winReward = null,
}) {
  const { t } = useTranslation();

  const winRewardBodyKey =
    resultOutcome === 'won' && winReward && typeof winReward.id === 'string'
      ? `exploreUnknownPage.minigame.beyondBird.winReward.${winReward.id}`
      : null;
  const [phase, setPhase] = useState('sequence');
  const [activePop, setActivePop] = useState(null);
  const pickedRef = useRef(false);
  const timersRef = useRef([]);

  const slots = treeSlots ?? BEYOND_BIRD_MEMORY_TREE_SLOTS;

  const clearTimers = useCallback(() => {
    for (const id of timersRef.current) {
      window.clearTimeout(id);
    }
    timersRef.current = [];
  }, []);

  useEffect(() => {
    if (slotEditMode) {
      clearTimers();
      setPhase('sequence');
      setActivePop(null);
      pickedRef.current = false;
      return undefined;
    }
    if (resultOutcome != null) {
      return undefined;
    }
    if (!challenge || !Array.isArray(challenge.sequence) || challenge.sequence.length < 1) {
      throw new Error('BeyondBirdMemoryMinigame: challenge.sequence is required');
    }
    clearTimers();
    setPhase('sequence');
    setActivePop(null);
    pickedRef.current = false;

    const { steps: stepTimings, recallTransitionMs } = getBeyondBirdMemoryAcceleratingStepTimings(
      challenge.sequence.length
    );

    let step = 0;
    const scheduleNext = () => {
      if (step >= challenge.sequence.length) {
        const doneTimer = window.setTimeout(() => {
          setActivePop(null);
          setPhase('recall');
        }, recallTransitionMs);
        timersRef.current.push(doneTimer);
        return;
      }
      const entry = challenge.sequence[step];
      const { gapBeforeMs, displayMs } = stepTimings[step];
      const showTimer = window.setTimeout(() => {
        setActivePop(entry);
        playSoundEffect(BEYOND_BIRD_MEMORY_APPEAR_SOUND_SRC);
        step += 1;
        const hideTimer = window.setTimeout(() => {
          scheduleNext();
        }, displayMs);
        timersRef.current.push(hideTimer);
      }, gapBeforeMs);
      timersRef.current.push(showTimer);
    };
    scheduleNext();

    return () => {
      clearTimers();
    };
  }, [challenge, clearTimers, slotEditMode, resultOutcome]);

  const handleTreeClick = useCallback(
    (treeIndex) => {
      if (phase !== 'recall' || disabled || pickedRef.current) return;
      if (!Number.isInteger(treeIndex) || treeIndex < 0) {
        throw new Error(`BeyondBirdMemoryMinigame: invalid treeIndex (${String(treeIndex)})`);
      }
      pickedRef.current = true;
      setPhase('done');
      onPickTree(treeIndex);
    },
    [phase, disabled, onPickTree]
  );

  const targetBirdId = challenge.targetBirdId;

  return (
    <div className="beyond-bird-memory" ref={containerRef}>
      <img
        src={BEYOND_BIRD_MEMORY_BACKGROUND_SRC}
        alt=""
        className="beyond-bird-memory-bg"
        draggable={false}
      />
      {slotEditMode ? (
        <>
          {slots.map((slot) => {
            const [cx, cy] = slot.center;
            return (
              <div
                key={`preview-${slot.id}`}
                className="beyond-bird-memory-tree beyond-bird-memory-tree--edit-preview"
                style={{
                  left: `${(cx / BEYOND_BIRD_MEMORY_LOGICAL_WIDTH) * 100}%`,
                  top: `${(cy / BEYOND_BIRD_MEMORY_LOGICAL_HEIGHT) * 100}%`,
                  width: `${(slot.hitRadius * 2) / BEYOND_BIRD_MEMORY_LOGICAL_WIDTH * 100}%`,
                  height: `${(slot.hitRadius * 2) / BEYOND_BIRD_MEMORY_LOGICAL_HEIGHT * 100}%`,
                }}
              >
                <BeyondBirdPopImage birdId={1} className="beyond-bird-memory-bird-pop" />
              </div>
            );
          })}
          {onSlotAuthoringChange ? (
            <BeyondBirdTreeSlotEditor
              containerRef={containerRef}
              slots={slots}
              onSlotChange={onSlotAuthoringChange}
            />
          ) : null}
        </>
      ) : (
        slots.map((slot) => {
          const [cx, cy] = slot.center;
          const isActivePop =
            activePop != null && activePop.treeIndex === slot.id && phase === 'sequence';
          return (
            <button
              key={slot.id}
              type="button"
              className={`beyond-bird-memory-tree${phase === 'recall' && !disabled ? ' beyond-bird-memory-tree--recall' : ''}`}
              style={{
                left: `${(cx / BEYOND_BIRD_MEMORY_LOGICAL_WIDTH) * 100}%`,
                top: `${(cy / BEYOND_BIRD_MEMORY_LOGICAL_HEIGHT) * 100}%`,
                width: `${(slot.hitRadius * 2) / BEYOND_BIRD_MEMORY_LOGICAL_WIDTH * 100}%`,
                height: `${(slot.hitRadius * 2) / BEYOND_BIRD_MEMORY_LOGICAL_HEIGHT * 100}%`,
              }}
              disabled={phase !== 'recall' || disabled}
              onClick={() => handleTreeClick(slot.id)}
              aria-label={t('exploreUnknownPage.minigame.beyondBird.treeAria', { index: slot.id + 1 })}
            >
              {isActivePop ? (
                <BeyondBirdPopImage birdId={activePop.birdId} className="beyond-bird-memory-bird-pop" />
              ) : null}
            </button>
          );
        })
      )}
      <div className="beyond-bird-memory-recall-panel" role="status">
        <div className="beyond-bird-memory-target-bird-wrap">
          <BeyondBirdPopImage birdId={targetBirdId} className="beyond-bird-memory-target-bird" />
        </div>
        <p className="beyond-bird-memory-recall-prompt">
          {slotEditMode
            ? t('exploreUnknownPage.minigame.beyondBird.slotEditHint')
            : resultOutcome === 'won'
              ? winRewardBodyKey
                ? t(winRewardBodyKey, {
                    percent: winReward.darknessReductionApplied,
                    xp: winReward.xpGained,
                    billyBucks: winReward.billyBucksAmount,
                    health: winReward.startHealthBonus != null ? winReward.startHealthBonus : 3,
                  })
                : t('exploreUnknownPage.minigame.beyondBird.success')
              : resultOutcome === 'lost'
                ? t('exploreUnknownPage.minigame.beyondBird.failure')
                : phase === 'done'
                  ? t('exploreUnknownPage.minigame.beyondBird.submitting')
                  : t('exploreUnknownPage.minigame.beyondBird.recallPrompt')}
        </p>
      </div>
      {resultOutcome != null ? (
        <div
          className={`beyond-bird-memory-result-banner beyond-bird-memory-result-banner--${resultOutcome}`}
          role="status"
          aria-live="polite"
        >
          <p className="beyond-bird-memory-result-banner-title">
            {resultOutcome === 'won'
              ? t('exploreUnknownPage.minigame.beyondBird.successTitle')
              : t('exploreUnknownPage.minigame.beyondBird.failureTitle')}
          </p>
          <p className="beyond-bird-memory-result-banner-body">
            {resultOutcome === 'won'
              ? winRewardBodyKey
                ? t(winRewardBodyKey, {
                    percent: winReward.darknessReductionApplied,
                    xp: winReward.xpGained,
                    billyBucks: winReward.billyBucksAmount,
                    health: winReward.startHealthBonus != null ? winReward.startHealthBonus : 3,
                  })
                : t('exploreUnknownPage.minigame.beyondBird.successBodyGeneric')
              : t('exploreUnknownPage.minigame.beyondBird.failureBody')}
          </p>
          {typeof onResultProceed === 'function' ? (
            <button
              type="button"
              className="beyond-bird-memory-result-proceed"
              onClick={() => onResultProceed()}
            >
              {t('exploreUnknownPage.minigame.proceed')}
            </button>
          ) : null}
        </div>
      ) : null}
      {showDevGrid && containerRef ? (
        <ExploreUnknownGridOverlay
          containerRef={containerRef}
          logicalWidth={BEYOND_BIRD_MEMORY_LOGICAL_WIDTH}
          logicalHeight={BEYOND_BIRD_MEMORY_LOGICAL_HEIGHT}
          gridSize={20}
          isVisible
          zIndex={600000}
          tooltipFooter={t('exploreUnknownPage.minigame.beyondBird.gridHint')}
        />
      ) : null}
    </div>
  );
}
